import React, { useState } from 'react';
import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Spinner, mergeClasses } from '@fluentui/react-components';
import { MoreHorizontal20Regular } from '@fluentui/react-icons';
import { GuideTooltip } from './GuideTooltip';
import './GuideComponents.css';

/*
 * Grilla de la guía ("Grilla"): encabezado 12 / Bold / MAYÚSCULAS en texto secundario sobre gris
 * tenue, orden con ▲/▼ (activo, en primario) o ↕ (ordenable), filas de 14 con hover neutro tenue,
 * seleccionada (primario tenue + barra azul a la izquierda), pendiente (alerta muy tenue), inactiva
 * (texto secundario), fila de total gris y bold, montos a la derecha con números tabulares y
 * acciones en ⋯ a la derecha. Es una <table> nativa con clases `ichtys-*` (GuideComponents.css).
 */

export type GuideSortDirection = 'asc' | 'desc';

export interface GuideSortState {
  key?: string;
  direction: GuideSortDirection;
}

type SortValue = string | number | Date | null | undefined;

const toComparable = (value: SortValue): string | number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value.getTime();
  return value;
};

/** Ordena sin mutar; los vacíos van siempre al final. Textos con orden natural en español. */
export const sortRows = <T,>(rows: T[], accessor: ((row: T) => SortValue) | undefined, direction: GuideSortDirection): T[] => {
  if (!accessor) return rows;
  const factor = direction === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, value: toComparable(accessor(row)) }))
    .sort((a, b) => {
      if (a.value === undefined && b.value === undefined) return a.index - b.index;
      if (a.value === undefined) return 1;
      if (b.value === undefined) return -1;
      const result =
        typeof a.value === 'number' && typeof b.value === 'number'
          ? a.value - b.value
          : String(a.value).localeCompare(String(b.value), 'es', { numeric: true, sensitivity: 'base' });
      return result === 0 ? a.index - b.index : result * factor;
    })
    .map((item) => item.row);
};

/**
 * Orden de una grilla: devuelve las filas ordenadas y `sortProps(key)` para cada `GuideTh`. Sin
 * `initial`, se respeta el orden original hasta que el usuario elige una columna; cada click alterna
 * ascendente / descendente.
 */
export const useGuideSort = <T,>(rows: T[], accessors: Record<string, (row: T) => SortValue>, initial?: GuideSortState) => {
  const [sort, setSort] = useState<GuideSortState>(initial ?? { direction: 'asc' });
  const sorted = sortRows(rows, sort.key ? accessors[sort.key] : undefined, sort.direction);
  const sortProps = (key: string): GuideThSort => ({
    direction: sort.key === key ? sort.direction : undefined,
    onSort: () =>
      setSort((current) => (current.key === key ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' })),
  });
  return { rows: sorted, sort, sortProps };
};

export interface GuideThSort {
  /** Dirección si es la columna activa; `undefined` si es ordenable pero no activa. */
  direction?: GuideSortDirection;
  onSort: () => void;
}

export interface GuideThProps extends Omit<React.ThHTMLAttributes<HTMLTableCellElement>, 'align'> {
  /** `end` para montos y números (a la derecha). */
  align?: 'start' | 'end';
  sort?: GuideThSort;
}

/** Encabezado de columna: MAYÚSCULAS 12 / Bold; si es ordenable, botón con ▲ / ▼ / ↕ y `aria-sort`. */
export const GuideTh = ({ align = 'start', sort, className, children, ...rest }: GuideThProps) => {
  const ariaSort = sort?.direction === 'asc' ? 'ascending' : sort?.direction === 'desc' ? 'descending' : sort ? 'none' : undefined;
  const indicator = sort?.direction === 'asc' ? '▲' : sort?.direction === 'desc' ? '▼' : '↕';
  return (
    <th {...rest} scope={rest.scope ?? 'col'} aria-sort={ariaSort} className={mergeClasses(align === 'end' && 'ichtys-cell--end', className)}>
      {sort ? (
        <button type="button" className="ichtys-sort" onClick={sort.onSort}>
          {children}
          <span className="ichtys-sort__indicator" aria-hidden="true">
            {indicator}
          </span>
        </button>
      ) : (
        children
      )}
    </th>
  );
};

export interface GuideRowState {
  selected?: boolean;
  pending?: boolean;
  inactive?: boolean;
  total?: boolean;
  detail?: boolean;
}

/** Clases de una fila según su estado (hover incluido). */
export const guideRowClassName = ({ selected, pending, inactive, total, detail }: GuideRowState = {}, extra?: string): string =>
  mergeClasses(
    !total && !detail && 'ichtys-row',
    selected && 'ichtys-row--selected',
    pending && 'ichtys-row--pending',
    inactive && 'ichtys-row--inactive',
    total && 'ichtys-row--total',
    detail && 'ichtys-row--detail',
    extra
  );

/** Clases de una celda: `num` (tabulares), `amount` (15 / Bold / tabulares), `end` (derecha), `warning` (texto alerta). */
export const guideCellClassName = (...kinds: Array<'num' | 'amount' | 'end' | 'warning' | 'check' | 'actions' | false | undefined>): string =>
  mergeClasses(...kinds.filter(Boolean).map((kind) => `ichtys-cell--${kind}`));

export interface GuideTableProps {
  ariaLabel: string;
  children: React.ReactNode;
  /** Pie dentro del recuadro (paginación). */
  footer?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
  /**
   * Scroll propio (spec 008): alto máximo del área de filas (p. ej. `calc(100vh - 360px)`), con scroll vertical y
   * horizontal adentro, encabezado fijo (sticky) y el pie (paginación) siempre visible fuera del scroll. Sin
   * `maxHeight` la grilla crece con sus filas (sólo scroll horizontal).
   */
  maxHeight?: string;
  /** Alto mínimo del área de filas con `maxHeight` (por defecto 360 px). */
  minHeight?: string;
  /** Primera columna de datos fija al desplazarse en horizontal (la de checkbox, si la hay, también). */
  stickyFirstColumn?: boolean;
  /**
   * Scroll propio que llena el alto disponible (spec 008): la grilla es un ítem flex (`flex: 1 1 auto`) de una cadena
   * de contenedores flex en columna con alto definido; las filas tienen scroll vertical y horizontal adentro, el
   * encabezado queda fijo y el pie fuera del scroll. Mínimo `minHeight` (por defecto 360 px, incluye el pie): si no
   * entra, scrollea el contenedor de la página. Así hay una sola barra vertical (sin `calc(100vh - X)`).
   */
  fill?: boolean;
  /** `data-testid` del contenedor con scroll. */
  scrollTestId?: string;
}

/** Recuadro de la grilla (borde, radio 4, scroll horizontal si no entra; con `fill` / `maxHeight`, scroll propio) con pie opcional. */

export const GuideTable = ({
  ariaLabel,
  children,
  footer,
  className,
  maxHeight,
  minHeight,
  stickyFirstColumn,
  fill,
  scrollTestId,
  'data-testid': testId,
}: GuideTableProps) => {
  const contained = !!maxHeight || !!fill;
  const style = maxHeight
    ? ({ '--ichtys-table-max-height': maxHeight, '--ichtys-table-min-height': minHeight ?? '360px' } as React.CSSProperties)
    : fill
      ? ({ '--ichtys-table-fill-min-height': minHeight ?? '360px' } as React.CSSProperties)
      : undefined;
  return (
    <div
      className={mergeClasses('ichtys-table-wrap', contained && 'ichtys-table-wrap--contained', fill && 'ichtys-table-wrap--fill', className)}
      style={fill ? style : undefined}
      data-testid={testId}
    >
      <div
        className={mergeClasses('ichtys-table-scroll', contained && 'ichtys-table-scroll--contained', fill && 'ichtys-table-scroll--fill')}
        style={maxHeight ? style : undefined}
        tabIndex={contained ? 0 : undefined}
        role={contained ? 'region' : undefined}
        aria-label={contained ? ariaLabel : undefined}
        data-testid={scrollTestId}
      >
        <table
          className={mergeClasses('ichtys-table', contained && 'ichtys-table--sticky-head', stickyFirstColumn && 'ichtys-table--sticky-first')}
          aria-label={ariaLabel}
        >
          {children}
        </table>
      </div>
      {footer}
    </div>
  );
};

export interface GuideRowAction {
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Acción destructiva ("Borrar"): en rojo y al final del menú. */
  danger?: boolean;
}

export interface GuideRowActionsProps {
  /** Nombre accesible y tooltip del botón ⋯ (p. ej. "Acciones de Liquidación enero"). */
  label: string;
  actions: GuideRowAction[];
  /** Muestra un spinner en lugar de ⋯ (acción en curso). */
  busy?: boolean;
  disabled?: boolean;
}

/** Acciones de una fila en ⋯ a la derecha (menú contextual; las destructivas en rojo, al final). */
export const GuideRowActions = ({ label, actions, busy, disabled }: GuideRowActionsProps) => {
  const ordered = [...actions.filter((action) => !action.danger), ...actions.filter((action) => action.danger)];
  return (
    <Menu positioning="below-end">
      <MenuTrigger disableButtonEnhancement>
        <GuideTooltip content={label} relationship="label">
          <Button
            appearance="subtle"
            className="ichtys-row-actions"
            icon={busy ? <Spinner size="extra-tiny" /> : <MoreHorizontal20Regular />}
            disabled={disabled || busy}
            aria-busy={busy || undefined}
          />
        </GuideTooltip>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {ordered.map((action) => (
            <MenuItem
              key={action.key}
              disabled={action.disabled}
              className={action.danger ? 'ichtys-menu-item--danger' : undefined}
              onClick={action.onClick}
            >
              {action.label}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};
